/**
 * fix-73: the Monday edits card's two buttons open the Knowledge screen with the text filled in, and nothing is saved
 * until the operator presses Save there — so the desk's guard runs on it as on any save.
 *
 *   rule       — a line appended to the Drafter instructions
 *   knowledge  — a new operator article from a fact he added
 *
 * Carried in the browser's session for one read. Never throws: storage can be missing or refused.
 */
export type Handoff =
  | { kind: 'rule'; slug: string; line: string; reason: string }
  | { kind: 'knowledge'; title: string; body: string; reason: string };

const KEY = 'knowledge-handoff';
type StoreLike = { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void };
const session = (): StoreLike | null => { try { return window.sessionStorage; } catch { return null; } };

export function handOff(h: Handoff, store: StoreLike | null = session()): void {
  try { store?.setItem(KEY, JSON.stringify(h)); } catch { /* nothing carried: the screen opens as it is */ }
}

/** The waiting hand-off for this editor, taken (read once); null when there is none or it is for another article. */
export function takeHandoff(want: { slug: string | null; isNew: boolean }, store: StoreLike | null = session()): Handoff | null {
  try {
    const h = JSON.parse(store?.getItem(KEY) ?? 'null') as Handoff | null;
    if (!h) return null;
    const fits = h.kind === 'rule' ? !want.isNew && want.slug === h.slug : want.isNew;
    if (!fits) return null;
    store?.removeItem(KEY);
    return h;
  } catch { return null; }
}

/** The body with a drafted rule line added as its own paragraph. */
export const withLine = (body: string, line: string) => `${body.replace(/\s+$/, '')}\n\n${line.trim()}\n`;

/** An article title from a fact: its first sentence, at most 200 characters. */
export const titleOf = (fact: string) => fact.split(/(?<=[.!?])\s/)[0].replace(/[.!?]+$/, '').trim().slice(0, 200);
